import apiClient from './api'

function cleanParams(params = {}) {
  const result = {}
  Object.entries(params).forEach(([key, value]) => {
    if (value == null) return
    if (typeof value === 'string' && value.trim() === '') return
    result[key] = value
  })
  return result
}

function asString(value, fallback = '') {
  if (value == null) return fallback
  return String(value)
}

function asNumber(value, fallback = null) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function normalizePolicy(row = {}) {
  return {
    id: asString(row.id),
    subjectType: asString(row.subjectType),
    subjectId: row.subjectId == null ? null : asString(row.subjectId),
    endpointGroup: asString(row.endpointGroup),
    windowSeconds: asNumber(row.windowSeconds, 3600),
    maxRequests: asNumber(row.maxRequests, 0),
    maxCostUnits: row.maxCostUnits == null ? null : asNumber(row.maxCostUnits, null),
    maxConcurrentJobs: row.maxConcurrentJobs == null ? null : asNumber(row.maxConcurrentJobs, null),
    dailyMaxRequests: row.dailyMaxRequests == null ? null : asNumber(row.dailyMaxRequests, null),
    monthlyMaxRequests: row.monthlyMaxRequests == null ? null : asNumber(row.monthlyMaxRequests, null),
    status: asString(row.status, 'active'),
    priority: asNumber(row.priority, 100),
    reason: row.reason == null ? null : asString(row.reason),
    createdByUserId: row.createdByUserId == null ? null : asString(row.createdByUserId),
    updatedByUserId: row.updatedByUserId == null ? null : asString(row.updatedByUserId),
    effectiveFrom: row.effectiveFrom || null,
    effectiveTo: row.effectiveTo || null,
    createdAt: row.createdAt || null,
    updatedAt: row.updatedAt || null,
  }
}

function normalizeDevice(row = {}) {
  return {
    id: asString(row.id),
    deviceIdHash: asString(row.deviceIdHash),
    deviceIdShort: asString(row.deviceIdShort || asString(row.deviceIdHash).slice(0, 10)),
    user: row.user || null,
    platform: asString(row.platform),
    appVersion: row.appVersion == null ? null : asString(row.appVersion),
    locale: row.locale == null ? null : asString(row.locale),
    timezone: row.timezone == null ? null : asString(row.timezone),
    pushProvider: asString(row.pushProvider, 'none'),
    hasPushToken: Boolean(row.hasPushToken),
    status: asString(row.status, 'active'),
    lastSeenAt: row.lastSeenAt || null,
    effectivePolicy: row.effectivePolicy ? normalizePolicy(row.effectivePolicy) : null,
    effectiveLimit: row.effectiveLimit || null,
  }
}

function normalizeDetail(row = {}) {
  return {
    device: row.device
      ? {
          ...normalizeDevice(row.device),
          lastIpHash: row.device.lastIpHash || null,
          lastUserAgentHash: row.device.lastUserAgentHash || null,
          metadata: row.device.metadata || null,
          createdAt: row.device.createdAt || null,
          updatedAt: row.device.updatedAt || null,
        }
      : null,
    linkedUser: row.linkedUser || null,
    effectivePolicy: row.effectivePolicy ? normalizePolicy(row.effectivePolicy) : null,
    effectiveLimit: row.effectiveLimit || null,
    usageBuckets: Array.isArray(row.usageBuckets) ? row.usageBuckets : [],
    usageSummary: row.usageSummary || {
      requestCount: 0,
      acceptedCount: 0,
      rejectedCount: 0,
      costUnits: 0,
    },
    notificationStats: row.notificationStats || {
      total: 0,
      pending: 0,
      sent: 0,
      failed: 0,
      cancelled: 0,
      recent: [],
    },
    latestPublishedRelease: row.latestPublishedRelease || null,
  }
}

function normalizeError(error, fallbackMessage) {
  return {
    status: Number(error?.response?.status || 0),
    message: error?.response?.data?.message || error?.message || fallbackMessage,
    code: error?.response?.data?.error || error?.response?.data?.code || null,
  }
}

export async function listMobileDevices(params = {}) {
  try {
    const response = await apiClient.get('/admin/mobile-devices', { params: cleanParams(params) })
    const rows = Array.isArray(response?.data?.data) ? response.data.data : []
    return rows.map(normalizeDevice)
  } catch (error) {
    throw normalizeError(error, 'Unable to load mobile devices')
  }
}

export async function getMobileDevice(id) {
  const deviceId = asString(id).trim()
  if (!deviceId) throw { status: 400, message: 'Device id is required' }

  try {
    const response = await apiClient.get(`/admin/mobile-devices/${deviceId}`)
    return normalizeDetail(response?.data?.data || response?.data)
  } catch (error) {
    throw normalizeError(error, 'Unable to load mobile device detail')
  }
}

export async function updateMobileDeviceStatus(id, status) {
  const deviceId = asString(id).trim()
  if (!deviceId) throw { status: 400, message: 'Device id is required' }

  try {
    const response = await apiClient.patch(`/admin/mobile-devices/${deviceId}/status`, { status })
    return normalizeDevice(response?.data?.data || response?.data)
  } catch (error) {
    throw normalizeError(error, 'Unable to update mobile device status')
  }
}

export async function updateMobileDeviceLimits(id, payload) {
  const deviceId = asString(id).trim()
  if (!deviceId) throw { status: 400, message: 'Device id is required' }

  try {
    const response = await apiClient.put(`/admin/mobile-devices/${deviceId}/limits`, payload)
    return normalizePolicy(response?.data?.data || response?.data)
  } catch (error) {
    throw normalizeError(error, 'Unable to update mobile device limits')
  }
}

export { normalizeDevice, normalizeDetail, normalizePolicy }
